import { Component, useState, type ErrorInfo, type ReactNode } from "react";

// UX-13: Existing DashboardErrorBoundary
interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

class DashboardErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return <h1>Something went wrong.</h1>;
    }

    return this.props.children;
  }
}

// UX-13: New SectionErrorBoundary component
interface SectionErrorBoundaryProps {
  children: ReactNode;
  /** For error logging; defaults to "page". */
  sectionName?: string;
}

interface SectionErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class SectionErrorBoundary extends Component<SectionErrorBoundaryProps, SectionErrorBoundaryState> {
  public state: SectionErrorBoundaryState = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): SectionErrorBoundaryState {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`Error in section: ${this.props.sectionName ?? "page"}`, error, errorInfo);
  }

  private resetError = () => {
    this.setState({ hasError: false, error: null });
  }

  public render() {
    if (this.state.hasError) {
      return <ErrorCard error={this.state.error} onRetry={this.resetError} />;
    }

    return this.props.children;
  }
}

const ErrorCard = ({ error, onRetry }: { error: Error | null, onRetry: () => void }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
      <div className="flex">
        <div className="py-1">
          <svg className="fill-current h-6 w-6 text-red-500 mr-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-5h2v2h-2v-2zm0-8h2v6h-2V5z"/></svg>
        </div>
        <div>
          <p className="font-bold">Something went wrong in this section</p>
          <div className="mt-2">
            <button onClick={onRetry} className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded text-xs">
              Retry
            </button>
            <button onClick={() => setShowDetails(!showDetails)} className="ml-2 text-xs text-gray-700 underline">
              {showDetails ? 'Hide Details' : 'Show Details'}
            </button>
          </div>
        </div>
      </div>
      {showDetails && error && (
        <div className="mt-4 bg-red-50 p-2 rounded">
          <pre className="text-xs whitespace-pre-wrap"><code>{error.stack}</code></pre>
        </div>
      )}
    </div>
  );
};

export { DashboardErrorBoundary, SectionErrorBoundary };
